/**
 * Standings column sets per sport (specs/multi-sport-core).
 *
 * A football table and a basketball table are not the same table with different numbers: one is
 * W/D/L + goals + points, the other is W/L + win percentage + games behind. The column SET is
 * what varies, so it is resolved from the sport definition rather than toggled per column.
 */

import { getSportDefinitionOrDefault } from '../sports.constants';

export interface StandingsColumn {
  /** Row field this column reads. */
  key: string;
  /** Full name, for accessibility labels. */
  label: string;
  /** Header abbreviation. */
  shortLabel: string;
  /** How to render the value; `ratio` is a 0–1 fraction shown as .625, `signed` prefixes +. */
  format: 'integer' | 'ratio' | 'signed' | 'halves';
}

/** Every column any sport can show, keyed by row field. */
const COLUMN_CATALOGUE: Record<string, Omit<StandingsColumn, 'key'>> = {
  played: { label: 'Played', shortLabel: 'P', format: 'integer' },
  won: { label: 'Won', shortLabel: 'W', format: 'integer' },
  drawn: { label: 'Drawn', shortLabel: 'D', format: 'integer' },
  lost: { label: 'Lost', shortLabel: 'L', format: 'integer' },
  goalsFor: { label: 'Goals for', shortLabel: 'GF', format: 'integer' },
  goalsAgainst: { label: 'Goals against', shortLabel: 'GA', format: 'integer' },
  goalDiff: { label: 'Goal difference', shortLabel: 'GD', format: 'signed' },
  points: { label: 'Points', shortLabel: 'PTS', format: 'integer' },
  winPct: { label: 'Win percentage', shortLabel: 'PCT', format: 'ratio' },
  gamesBehind: { label: 'Games behind', shortLabel: 'GB', format: 'halves' },
  // Formula One. These are the field names `formulaOneStandings` actually emits per row, which
  // is what a catalogue key means — NOT aliases of football's `won` / basketball's `gamesBehind`.
  // A drivers' championship counts race wins, not matches won, and measures the gap in points
  // rather than games, so they are different columns that happen to rhyme.
  wins: { label: 'Race wins', shortLabel: 'W', format: 'integer' },
  // `halves` renders the leader's 0 as an em dash and keeps a half point readable — F1 has
  // awarded half points for a shortened race.
  behind: { label: 'Points behind leader', shortLabel: 'GAP', format: 'halves' },
};

/**
 * Columns a standings table should render for a sport, in display order.
 *
 * @param sportKey sport of the competition; falls back to football when absent
 */
export function getStandingsColumns(sportKey: string | null | undefined): StandingsColumn[] {
  const definition = getSportDefinitionOrDefault(sportKey);
  const unknown = definition.standingsColumns.filter((key) => COLUMN_CATALOGUE[key] == null);

  // A declared column with no catalogue entry used to be dropped in silence, which is how the
  // Formula One table lost `wins` and `behind` and rendered as position + driver + PTS with no
  // throw and no warning. Dropping is still the right RUNTIME behaviour — a missing column must
  // not blank the whole table — but it is never right for it to be invisible.
  // `standingsColumnsAreCatalogued` in the test suite is the real guard; this is the backstop.
  if (unknown.length > 0 && typeof __DEV__ === 'boolean' && __DEV__) {
    console.warn(
      `[standings] ${definition.key} declares column(s) missing from COLUMN_CATALOGUE: `
      + `${unknown.join(', ')}. They will not render.`,
    );
  }

  return definition.standingsColumns
    .filter((key) => COLUMN_CATALOGUE[key] != null)
    .map((key) => ({ key, ...COLUMN_CATALOGUE[key] }));
}

/**
 * Every column key the catalogue knows. Exported for the coverage test — the parity test compares
 * the client's declared list against the server's and so cannot see a key both sides agree on
 * that the catalogue has never heard of.
 */
export const CATALOGUED_COLUMN_KEYS: readonly string[] = Object.keys(COLUMN_CATALOGUE);

/**
 * Render a standings value the way its column expects.
 *
 * `.625` rather than `0.625` for win percentage, `+7` for goal difference, and `—` for the
 * leader's zero games behind (a literal 0 reads as a score, not as "top of the table").
 */
export function formatStandingsValue(
  value: number | null | undefined,
  format: StandingsColumn['format'],
): string {
  if (value == null || Number.isNaN(value)) return '–';

  switch (format) {
    case 'ratio':
      return value.toFixed(3).replace(/^0/, '');
    case 'signed':
      return value > 0 ? `+${value}` : String(value);
    case 'halves':
      if (value === 0) return '—';
      return Number.isInteger(value) ? String(value) : value.toFixed(1);
    case 'integer':
    default:
      return String(Math.trunc(value));
  }
}
